import { api } from './apiService';

export type ListenTargetKind = 'handle' | 'hashtag' | 'topic';

export type ListenPlatform = 'x' | 'instagram' | 'facebook' | 'tiktok';

export type ScheduleWindow = {
  /** ISO timestamps; window is [start, end). */
  start: string;
  end: string;
  intervalMinutes: number;
};

export type ListeningSchedule = {
  windows: ScheduleWindow[];
  /** Interval outside every window; 0 = off. */
  defaultIntervalMinutes: number;
};

export type ListeningConfig = {
  id?: string;
  profileId: string;
  enabled: boolean;
  /** Absent = legacy: hourly, always. */
  schedule?: ListeningSchedule;
  lastScheduledAt?: string;
  /** Computed by the server: interval in effect now (0 = off). */
  currentIntervalMinutes: number;
  nextRunAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type ListenTarget = {
  id: string;
  profileId: string;
  kind: ListenTargetKind;
  value: string;
  label?: string;
  platforms: ListenPlatform[] | string[];
  enabled: boolean;
  excludeTerms?: string[];
  requireAny?: string[];
  queryRewrite?: string;
  facebookPageId?: string;
  lastPolledAt?: string;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type SentimentLexicon = {
  id?: string;
  profileId: string;
  positivePhrases: string[];
  negativePhrases: string[];
  createdAt?: string;
  updatedAt?: string;
};

/** Empty means nobody has decided yet. */
export type ListeningReviewStatus = 'confirmed' | 'dismissed' | '';

export type ListeningPost = {
  id: string;
  profileId: string;
  targetId?: string;
  platform: string;
  author?: string;
  text: string;
  url?: string;
  postedAt: string;
  sentiment: 'Positive' | 'Negative' | 'Neutral' | string;
  sentimentScore: number;
  matchedPhrases: string[];
  reviewStatus?: ListeningReviewStatus;
  reviewedAt?: string;
  reviewedBy?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type PollResult = {
  fetched: number;
  inserted: number;
  skipped: number;
  errors: number;
};

export async function getListeningConfig(profileId: string): Promise<ListeningConfig> {
  return (await api.get('/api/social-listening/config', { params: { profileId } })) as ListeningConfig;
}

export async function setListeningEnabled(profileId: string, enabled: boolean): Promise<ListeningConfig> {
  return (await api.put('/api/social-listening/config', { profileId, enabled })) as ListeningConfig;
}

export async function putListeningSchedule(
  profileId: string,
  schedule: ListeningSchedule
): Promise<ListeningConfig> {
  return (await api.put('/api/social-listening/config', { profileId, schedule })) as ListeningConfig;
}

export async function listListenTargets(profileId: string): Promise<ListenTarget[]> {
  const res = (await api.get('/api/social-listening/targets', { params: { profileId } })) as {
    targets?: ListenTarget[];
  };
  return res.targets ?? [];
}

export async function createListenTarget(body: {
  profileId: string;
  kind: ListenTargetKind;
  value: string;
  label?: string;
  platforms?: string[];
  excludeTerms?: string[];
  requireAny?: string[];
  queryRewrite?: string;
  facebookPageId?: string;
}): Promise<ListenTarget> {
  return (await api.post('/api/social-listening/targets', body)) as ListenTarget;
}

export async function deleteListenTarget(id: string): Promise<void> {
  await api.delete(`/api/social-listening/targets/${id}`);
}

export async function getSentimentLexicon(profileId: string): Promise<SentimentLexicon> {
  return (await api.get('/api/social-listening/lexicon', { params: { profileId } })) as SentimentLexicon;
}

export async function putSentimentLexicon(body: {
  profileId: string;
  positivePhrases: string[];
  negativePhrases: string[];
}): Promise<SentimentLexicon> {
  return (await api.put('/api/social-listening/lexicon', body)) as SentimentLexicon;
}

export async function listListeningPosts(
  profileId: string,
  opts?: { sentiment?: string; targetId?: string }
): Promise<ListeningPost[]> {
  const res = (await api.get('/api/social-listening/posts', {
    params: { profileId, ...opts },
  })) as { posts?: ListeningPost[] };
  return res.posts ?? [];
}

export type PollJobStatus = 'running' | 'completed' | 'failed';

export type PollJob = {
  id: string;
  profileId: string;
  trigger: 'manual' | 'cron';
  status: PollJobStatus;
  /** target × platform fetches */
  total: number;
  done: number;
  result: PollResult;
  error?: string;
  startedAt: string;
  finishedAt?: string;
};

/** Starts a background poll (or returns the one already running); follow it with getPollJob. */
export async function pollListeningNow(profileId: string): Promise<PollJob> {
  return (await api.post('/api/social-listening/poll', { profileId })) as PollJob;
}

export async function getPollJob(jobId: string): Promise<PollJob> {
  return (await api.get(`/api/social-listening/poll/jobs/${jobId}`)) as PollJob;
}

export async function getLatestPollJob(profileId: string): Promise<PollJob | null> {
  const res = (await api.get('/api/social-listening/poll/jobs/latest', { params: { profileId } })) as {
    job: PollJob | null;
  };
  return res.job ?? null;
}

export async function reanalyzeListeningPosts(profileId: string): Promise<{ updated: number }> {
  return (await api.post('/api/social-listening/posts/reanalyze', { profileId })) as { updated: number };
}

/** Records a human decision on one mention. Pass an empty status to clear it. */
export async function reviewListeningPost(
  id: string,
  status: ListeningReviewStatus
): Promise<ListeningPost> {
  return (await api.patch(`/api/social-listening/posts/${id}/review`, { status })) as ListeningPost;
}
